import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Pendencias } from '../Pendencias';
import { useSessao } from '../sessao';
import { api, ErroApi } from '../api';
import {
  AncoraDePendencias, CabecalhoDaFicha, EscolhaDeBalanca, EtapasDaFicha,
  FaixaDaFicha, JanelaDeDescarte, LaudoDaFicha, RodapeDeEtapas,
  fichaCongelada, useEquipamentos, useEtapa, useFicha,
} from '../Ficha';
import type { EquipamentoDaLista, Etapa, Indicador } from '../Ficha';
import { FotosDoRegistro } from '../FotosDoRegistro';
import {
  GraficoDaEnvoltoriaCd, GraficoDoTracado, GraficosDoCisalhamentoCd,
  SIGLA_CRITERIO_CD,
} from './CisalhamentoDiretoGraficos';
import {
  verCisalhamentoDireto, verCisalhamentoDiretoCp, gravarCisalhamentoDiretoFicha,
  criarCisalhamentoDiretoCp, editarCisalhamentoDiretoCp,
  gravarCisalhamentoDiretoUmidade, descartarCisalhamentoDiretoCapsula,
  colarLeiturasCisalhamentoDireto, descartarLeiturasCisalhamentoDireto,
  tracarCisalhamentoDireto, gravarCriteriosNoLaudoCd,
  importarPlanilhaCisalhamentoDireto,
} from '../api/cisalhamentoDireto';
import type {
  EnsaioCisalhamentoDireto, CorpoDeProvaCd, ConferenciaCd,
} from '../api/cisalhamentoDireto';
import { numero, paraNumero, paraCampo, plural, rotulo } from '../formato';
import {
  Area, Aviso, Campo, CampoNumerico, Carregando, ErroDaApi, Escolha,
  EscolhaPorCodigo, Selo, TabelaDeLancamento, Vazio, ZonaDeArquivo,
  RespostaDaAcao,
} from '../ui';
import type { Opcao } from '../ui';
import {
  LembreteDaEstufa, SecaDaLinha, WDaLinha, naEstufaDaFicha, useEstufaDoEnsaio,
} from './NaEstufa';
import type { Estufa } from './NaEstufa';

/**
 * Lançar e interpretar o cisalhamento direto — CD.IN, CD.NAT e CD.GD
 * (onda 265, RN-225).
 *
 * «Crie a ficha e interpretação do ensaio de cisalhamento direto,
 * seguindo como exemplo e padrão as telas do triaxial CIU» — Humberto,
 * 03/10/2026. A mesma espinha (`Ficha.tsx`): o corpo de prova escolhido
 * nos chips vale para todas as etapas, e as etapas são as do ensaio —
 * moldar, adensar, cisalhar —, mais a interpretação, onde moram TODOS os
 * gráficos («os gráficos de ensaio devem ser apresentados na parte de
 * interpretação»), e o laudo.
 *
 * ─── O que a tela NÃO calcula ────────────────────────────────────────
 *
 * Nada. Área do anel, γ, e₀, τ e σ (nominal e corrigida), o ponto de
 * cada critério, a envoltória, a velocidade real, t₉₀, t₁₀₀ e as
 * velocidades máximas vêm prontos do banco (`D-29`). A tela lê a
 * colagem da planilha e desenha; quem conta é a `0236`.
 */

const FASES = [
  { chave: 'amostra', texto: 'Amostra' },
  { chave: 'adensamento', texto: 'Adensamento' },
  { chave: 'cisalhamento', texto: 'Cisalhamento' },
  { chave: 'interpretacao', texto: 'Interpretação' },
  { chave: 'laudo', texto: 'Fotos e laudo' },
] as const;

/* Os critérios de ruptura — os mesmos de `fn_cd_criterios` (0236). */
const CRITERIOS: Opcao[] = [
  { valor: 'max_cisalhante', texto: 'τ máximo (pico)', detalhe: 'ASTM D3080 3.2.1' },
  { valor: 'deslocamento_relativo_10', texto: 'τ a 10 % de δh/L',
    detalhe: 'sem pico — ASTM D3080 3.2.1 e 10.2.4' },
  { valor: 'deslocamento_limite', texto: 'τ no deslocamento declarado',
    detalhe: 'o deslocamento limite do ensaio' },
  { valor: 'manual', texto: 'Manual (apontar)', detalhe: 'quem analisa aponta a leitura' },
];

const CRITERIO_TEXTO: Record<string, string> = {
  max_cisalhante: 'tensão cisalhante máxima — o pico (ASTM D3080, 3.2.1)',
  deslocamento_relativo_10:
    'tensão a 10 % de deslocamento relativo — sem pico (ASTM D3080, 3.2.1 e 10.2.4)',
  deslocamento_limite: 'tensão no deslocamento horizontal declarado no ensaio',
  manual: 'ponto apontado por quem analisa',
};

const CONDICOES: Opcao[] = [
  { valor: 'inundado', texto: 'Inundado' },
  { valor: 'umidade_natural', texto: 'Umidade natural' },
];

const TIPOS_AMOSTRA: Opcao[] = [
  { valor: 'indeformada', texto: 'Indeformada' },
  { valor: 'remoldada', texto: 'Remoldada' },
  { valor: 'compactada', texto: 'Compactada' },
];

const SITUACOES: Opcao[] = [
  { valor: 'moldado', texto: 'Moldado' },
  { valor: 'adensando', texto: 'Adensando' },
  { valor: 'cisalhado', texto: 'Cisalhado' },
  { valor: 'descartado', texto: 'Descartado' },
];

const BASES: Opcao[] = [
  { valor: 'nominal', texto: 'Nominal', detalhe: 'ASTM D3080 — τ = F/A, σ = N/A' },
  { valor: 'corrigida', texto: 'Corrigida', detalhe: 'τ e σ sobre a área de contato' },
];

const n = (v: unknown, casas: number, cortar = false) =>
  numero(v, casas, cortar) || '—';

type Gravar = (marca: string, f: () => Promise<unknown>) => Promise<boolean>;

export function CisalhamentoDireto() {
  const { id = '' } = useParams();
  const [d, setD] = useState<EnsaioCisalhamentoDireto | null>(null);
  const [cp, setCp] = useState<CorpoDeProvaCd | null>(null);
  const [cpId, setCpId] = useState<string | null>(null);
  const equipamentos = useEquipamentos();
  const [pendencias, setPendencias] = useState<any[]>([]);
  const [erro, setErro] = useState<unknown>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [salvando, setSalvando] = useState<string | null>(null);
  /* Sobe a cada gravação: a interpretação recarrega as curvas de todos. */
  const [versao, setVersao] = useState(0);

  const carregar = useCallback(async () => {
    try {
      const e = await verCisalhamentoDireto(id);
      setD(e);
      setCpId((atual) => {
        if (atual && e.corpos.some((c: any) => c.id === atual)) return atual;
        return e.corpos[0]?.id ?? null;
      });
    } catch (e) { setErro(e); }
  }, [id]);

  useEffect(() => { void carregar(); }, [carregar]);

  useEffect(() => {
    if (!cpId) { setCp(null); return; }
    void (async () => {
      try { setCp(await verCisalhamentoDiretoCp(cpId)); } catch (e) { setErro(e); }
    })();
  }, [cpId]);

  useEffect(() => {
    void (async () => {
      try {
        setPendencias(await api<any[]>(`/ensaios/${id}/pendencias`));
      } catch { setPendencias([]); }
    })();
  }, [id, d]);

  /**
   * Grava e recarrega as DUAS visões — o CP e o ensaio andam juntos:
   * mexer no critério de um CP muda o ponto dele, que muda a envoltória.
   */
  const gravar = useCallback<Gravar>(async (marca, chamada) => {
    setSalvando(marca); setErro(null); setOk(null);
    try {
      await chamada();
      await carregar();
      if (cpId) setCp(await verCisalhamentoDiretoCp(cpId));
      setVersao((v) => v + 1);
      setOk('Gravado.');
      return true;
    } catch (e) { setErro(e); return false; }
    finally { setSalvando(null); }
  }, [carregar, cpId]);

  const recarregar = useCallback(async () => {
    await carregar();
    if (cpId) setCp(await verCisalhamentoDiretoCp(cpId));
    setVersao((v) => v + 1);
  }, [carregar, cpId]);

  const campoErro = (c: string) =>
    (erro instanceof ErroApi ? erro.campo(c) : undefined);

  const ficha = useFicha(id, undefined, ok);
  /* As cápsulas na estufa e o carimbo das pesagens (onda 294, RN-241). */
  const estufa = useEstufaDoEnsaio(id, versao);
  /*
   * A Interpretação é de quem tem `ensaio.interpretar` (RN-238): «não
   * pode ser vista e editada, apenas pelos usuários que tiverem a
   * permissão» — Humberto, 06/10/2026. Para os demais, a fase some.
   */
  const { pode } = useSessao();
  const fases = pode('ensaio.interpretar')
    ? FASES : FASES.filter((f) => f.chave !== 'interpretacao');
  const vivos = (d?.corpos ?? []).filter((c: any) => c.situacao !== 'descartado');
  const semAdens = vivos.filter((c: any) => !Number(c.leituras_adensamento)).length;
  const semCis = vivos.filter((c: any) => !Number(c.leituras_cisalhamento)).length;
  const nominais = (d?.rupturas ?? []).filter((r: any) => r.base === 'nominal');
  const rompidos = nominais.filter((r: any) => !r.motivo_sem_ruptura
    && r.situacao !== 'descartado').length;
  const env = (d?.envoltorias ?? []).find((e: any) => e.base === 'nominal') ?? null;
  const envC = (d?.envoltorias ?? []).find((e: any) => e.base === 'corrigida') ?? null;
  const temEnvoltoria = !!env && !env.motivo_sem_envoltoria;
  const [etapa, irPara] = useEtapa(fases.length,
    !d?.cabecalho || vivos.length === 0 ? 1 : semAdens ? 2 : semCis ? 3 : 4);

  if (erro && !d) return <ErroDaApi erro={erro} />;
  if (!d) return <Carregando texto="Abrindo o ensaio" />;

  const cab = d.cabecalho ?? null;
  const fase = fases[etapa - 1].chave;
  const congelado = fichaCongelada(ficha?.status);
  const travam = pendencias.filter((p: any) => p.trava && !p.justificada);
  const tracados = (d.velocidades ?? []).filter((v: any) => v.situacao !== 'descartado'
    && v.t100_min != null).length;

  const etapas: Etapa[] = fases.map((f) => ({
    nome: f.texto,
    feita: f.chave === 'amostra' ? vivos.length > 0 && !!d.balanca
      : f.chave === 'adensamento' ? vivos.length > 0 && semAdens === 0
        : f.chave === 'cisalhamento' ? vivos.length > 0 && semCis === 0
          : f.chave === 'interpretacao' ? temEnvoltoria
            : ficha?.status === 'aprovado',
    falta: f.chave === 'amostra' && travam.some((p: any) =>
      String(p.codigo).startsWith('balanca_') || p.codigo === 'cp_sem_anel'),
    resumo: f.chave === 'amostra' ? plural(vivos.length, 'corpo de prova', 'corpos de prova')
      : f.chave === 'adensamento' ? `${vivos.length - semAdens} de ${vivos.length} com leituras`
        : f.chave === 'cisalhamento' ? `${vivos.length - semCis} de ${vivos.length} com leituras`
          : f.chave === 'interpretacao'
            ? (temEnvoltoria ? `${rompidos} rupturas · ${tracados} traçados` : 'precisa de dois CPs')
            : undefined,
  }));

  const indicadores: Indicador[] = [
    { rotulo: 'φ nominal', chave: true, unidade: '°',
      valor: temEnvoltoria ? n(env.phi_graus, 2) : null,
      porque: env?.motivo_sem_envoltoria ?? 'A envoltória precisa de dois pontos de ruptura.',
      como: <>Da reta τ = c + σ·tg φ ajustada aos pontos de ruptura, com τ e σ
        nominais da ASTM D3080 (área do anel calibrado).</> },
    { rotulo: 'c nominal', chave: true, unidade: 'kPa',
      valor: temEnvoltoria ? n(env.coesao_kpa, 2) : null,
      porque: env?.motivo_sem_envoltoria ?? 'A envoltória precisa de dois pontos de ruptura.' },
    { rotulo: 'R²', valor: temEnvoltoria ? n(env.r2, 4) : null },
    { rotulo: 'φ corrigida', unidade: '°',
      valor: envC && !envC.motivo_sem_envoltoria ? n(envC.phi_graus, 2) : null,
      nota: 'área de contato' },
    { rotulo: 'Corpos de prova', valor: String(vivos.length),
      nota: d.pacote?.cps_por_ensaio ? `de ${d.pacote.cps_por_ensaio} do pacote` : undefined },
    { rotulo: 'Com ruptura', valor: String(rompidos) },
  ];

  return (
    <div className="tela cisalhamento-direto ficha-carbon">
      <CabecalhoDaFicha id={id} sigla={ficha?.metodo_codigo ?? d.metodoCodigo ?? 'CD'}
        titulo="Cisalhamento direto" ficha={ficha} normaDaTela="ASTM D3080" />
      {cab && (
        <FaixaDaFicha indicadores={indicadores}
          itens={pendencias.filter((p) => !p.justificada).map((p) => p.texto)}
          pendencias={pendencias} />
      )}
      {cab && <EtapasDaFicha etapas={etapas} atual={etapa} irPara={irPara} />}

      <AncoraDePendencias>
        <Pendencias itens={pendencias.filter((p) => !p.justificada)
          .map((p) => p.texto)} id={id} detalhe={pendencias}
          aoJustificar={carregar} />
      </AncoraDePendencias>

      <RespostaDaAcao erro={erro} ok={ok ?? null} />
      {congelado && (
        <Aviso tipo="info" titulo={`Ensaio ${rotulo(ficha?.status).toLowerCase()} — só leitura`}>
          {ficha?.status === 'aprovado'
            ? 'Dado de ensaio aprovado não se altera. Correção é nova revisão: peça a reabertura a quem aprova.'
            : 'Ensaio encerrado não recebe dado novo. Se o serviço for refeito, é ensaio novo.'}
        </Aviso>
      )}

      {!cab ? (
        <FichaNova id={id} metodo={d.metodoCodigo} gravar={gravar} salvando={salvando} />
      ) : (
        <>
          <fieldset className="so-leitura" disabled={congelado}>
            {fase === 'laudo' ? (
              <>
                <FotosDoRegistro
                  id={id}
                  titulo="Registro fotográfico"
                  dica="O corpo de prova moldado no anel e depois de cisalhado — o plano de ruptura ajuda a conferir o ensaio depois."
                  roteiro={[
                    { tipo: 'cp_antes', nome: 'CP moldado', quando: 'antes do ensaio' },
                    { tipo: 'cp_depois', nome: 'CP cisalhado', quando: 'na desmontagem' },
                  ]}
                />
                <LaudoDaFicha id={id} />
              </>
            ) : (
              <>
                <SeletorDeCp d={d} cpId={cpId} aoTrocar={setCpId}
                  id={id} gravar={gravar} salvando={salvando} />
                {fase === 'interpretacao' ? (
                  <FaseInterpretacao d={d} cp={cp} cpId={cpId} id={id}
                    gravar={gravar} salvando={salvando} versao={versao} />
                ) : !cpId ? (
                  <Vazio>
                    Nenhum corpo de prova ainda. Crie o primeiro acima — as
                    etapas são dele.
                  </Vazio>
                ) : !cp ? (
                  <Carregando texto="Abrindo o corpo de prova" />
                ) : (
                  <>
                    {fase === 'amostra' && (
                      <FaseAmostra d={d} cab={cab} cp={cp} equipamentos={equipamentos}
                        gravar={gravar} salvando={salvando} campoErro={campoErro}
                        id={id} recarregar={recarregar}
                        estufa={estufa} congelado={congelado} />
                    )}
                    {fase === 'adensamento' && (
                      <FaseAdensamento cp={cp} gravar={gravar} salvando={salvando} />
                    )}
                    {fase === 'cisalhamento' && (
                      <FaseCisalhamento cp={cp} gravar={gravar} salvando={salvando} />
                    )}
                  </>
                )}
              </>
            )}
          </fieldset>
          <RodapeDeEtapas etapas={etapas} atual={etapa} irPara={irPara}
            nota={ok ? 'Gravado — o resultado acima já é o do servidor.' : undefined} />
        </>
      )}
    </div>
  );
}

// ══ A ficha, quando ainda não existe ══════════════════════════════════

function FichaNova({ id, metodo, gravar, salvando }: {
  id: string; metodo: string; gravar: Gravar; salvando: string | null;
}) {
  const [condicao, setCondicao] = useState(
    metodo === 'CD.IN' ? 'inundado' : 'umidade_natural');
  return (
    <section className="bloco">
      <h2>Abrir a ficha do cisalhamento direto</h2>
      <p className="explicacao">
        A condição vem do método ({metodo}): o CD.IN é inundado; o CD.NAT
        e o CD.GD, na umidade natural. Confirme e abra a ficha — o resto se
        preenche na etapa «Amostra».
      </p>
      <Escolha rotulo="Condição do ensaio" opcoes={CONDICOES} valor={condicao}
        aoEscolher={(v) => setCondicao(v ?? condicao)} limpavel={false} />
      <button type="button" className="primario"
        disabled={salvando === 'ficha'}
        onClick={() => gravar('ficha',
          () => gravarCisalhamentoDiretoFicha(id, { condicao }))}>
        {salvando === 'ficha' ? 'Abrindo…' : 'Abrir a ficha'}
      </button>
    </section>
  );
}

// ══ O seletor de corpo de prova ═══════════════════════════════════════

function SeletorDeCp({ d, cpId, aoTrocar, id, gravar, salvando }: {
  d: EnsaioCisalhamentoDireto;
  cpId: string | null;
  aoTrocar(v: string): void;
  id: string;
  gravar: Gravar;
  salvando: string | null;
}) {
  const proxima = (d.corpos.reduce(
    (m: number, c: any) => Math.max(m, Number(c.ordem) || 0), 0)) + 1;
  const previstos = d.pacote?.cps_por_ensaio ?? null;
  const vivos = d.corpos.filter((c: any) => c.situacao !== 'descartado').length;
  const faltam = previstos ? Math.max(previstos - vivos, 0) : 0;
  const rotuloCp = (o: number) => `CP-${String(o).padStart(2, '0')}`;
  const criarOsQueFaltam = async () => {
    for (let k = 0; k < faltam; k++) {
      const o = proxima + k;
      await criarCisalhamentoDiretoCp(id, { ordem: o, identificacao: rotuloCp(o) });
    }
  };
  return (
    <div className="seletor-cp">
      {previstos && (
        <p className="pacote-cp" data-pacote={d.pacote?.item_codigo}>
          <b>Pacote {d.pacote?.item_codigo}</b> · {previstos} corpos de prova
          {' '}— {vivos} {vivos === 1 ? 'criado' : 'criados'}
          {faltam > 0 && (
            <button type="button" className="secundario"
              disabled={salvando === 'pacote-cp'}
              onClick={() => gravar('pacote-cp', criarOsQueFaltam)}>
              {salvando === 'pacote-cp' ? 'Criando…'
                : `Criar ${faltam === 1 ? 'o que falta' : `os ${faltam} que faltam`}`}
            </button>
          )}
        </p>
      )}
      <div className="chips-cp" role="tablist" aria-label="Corpos de prova">
        {d.corpos.map((c: any) => (
          <button key={c.id} type="button" role="tab"
            aria-selected={c.id === cpId}
            className={`chip-cp${c.id === cpId ? ' corrente' : ''}${
              c.situacao === 'descartado' ? ' descartado' : ''}`}
            onClick={() => aoTrocar(c.id)}>
            <b>{c.identificacao}</b>
            <span className="sub">σ {n(c.tensao_normal_kpa, 0)} kPa</span>
          </button>
        ))}
        <button type="button" className="chip-cp novo"
          disabled={salvando === 'novo-cp'}
          onClick={() => gravar('novo-cp', () => criarCisalhamentoDiretoCp(id, {
            ordem: proxima, identificacao: rotuloCp(proxima),
          }))}>
          + Corpo de prova
        </button>
      </div>
    </div>
  );
}

// ══ Etapa 1 · a amostra, o corpo de prova e a umidade ═════════════════

function FaseAmostra({ d, cab, cp, equipamentos, gravar, salvando, campoErro, id,
  recarregar, estufa, congelado }: {
  d: EnsaioCisalhamentoDireto;
  cab: any;
  cp: CorpoDeProvaCd;
  equipamentos: EquipamentoDaLista[];
  gravar: Gravar;
  salvando: string | null;
  campoErro(c: string): string | undefined;
  id: string;
  recarregar(): Promise<void>;
  estufa: Estufa;
  congelado: boolean;
}) {
  const capsulas = equipamentos.filter((e) => e.categoria === 'capsula');
  const daEstufa = { estufa, congelado, recarregar };
  const doCp = cp.umidade.filter((u: any) => u.etapa === 'inicial');
  return (
    <>
      <FichaDoEnsaio d={d} cab={cab} id={id} gravar={gravar} salvando={salvando}
        campoErro={campoErro} equipamentos={equipamentos} />
      <DadosDoCp cp={cp} cab={cab} gravar={gravar} salvando={salvando}
        campoErro={campoErro} equipamentos={equipamentos} />
      <Capsulas id={id} cpId={null} etapa="inicial" linhas={d.umidadeComum}
        capsulas={capsulas} gravar={gravar} salvando={salvando} {...daEstufa}
        titulo="Umidade inicial — comum a todos os corpos de prova"
        explicacao="Um jogo de cápsulas da amostra moldada vale para todos os CPs que não tiverem o seu. A tara vem da verificação vigente da cápsula — não se digita." />
      <Capsulas id={id} cpId={cp.cp.id} etapa="inicial" linhas={doCp}
        capsulas={capsulas} gravar={gravar} salvando={salvando} {...daEstufa}
        titulo={`Umidade inicial — só do ${cp.cp.identificacao}`}
        explicacao="Opcional: se este CP tiver cápsulas próprias, valem elas, e não as comuns." />
      <Indices cp={cp.cp} />
      <Capsulas id={id} cpId={cp.cp.id} etapa="final"
        linhas={cp.umidade.filter((u: any) => u.etapa === 'final')}
        capsulas={capsulas} gravar={gravar} salvando={salvando} {...daEstufa}
        titulo={`Umidade final — ${cp.cp.identificacao}`}
        explicacao="Do corpo de prova depois de cisalhado." />
      <ImportarPlanilha d={d} id={id} recarregar={recarregar} />
    </>
  );
}

function FichaDoEnsaio({ d, cab, id, gravar, salvando, campoErro, equipamentos }: {
  d: EnsaioCisalhamentoDireto;
  cab: any;
  id: string;
  gravar: Gravar;
  salvando: string | null;
  campoErro(c: string): string | undefined;
  equipamentos: EquipamentoDaLista[];
}) {
  const inicial = () => ({
    condicao: cab.condicao as string,
    tipoAmostra: (cab.tipo_amostra ?? null) as string | null,
    energiaCompactacao: cab.energia_compactacao ?? '',
    tempoSaturacaoH: paraCampo(cab.tempo_saturacao_h),
    massaEspecificaGraos: paraCampo(cab.massa_especifica_graos),
    graosAssumida: !!cab.graos_assumida,
    areaConfiguradaMm2: paraCampo(cab.area_configurada_mm2),
    deslocamentoRupturaEstimadoMm: paraCampo(cab.deslocamento_ruptura_estimado_mm),
    deslocamentoLimiteMm: paraCampo(cab.deslocamento_limite_mm),
    criterioRuptura: (cab.criterio_ruptura ?? null) as string | null,
    criterioRupturaFonte: cab.criterio_ruptura_fonte ?? '',
    observacoes: cab.observacoes ?? '',
    prensa: d.prensa?.id ?? '',
    balanca: d.balanca?.id ?? '',
    balancaUmidade: d.balancaUmidade?.id ?? '',
  });
  const [f, setF] = useState(inicial);
  const set = (k: string, v: unknown) => setF((x) => ({ ...x, [k]: v }));
  useEffect(() => {
    setF((x) => ({ ...x, prensa: d.prensa?.id ?? '', balanca: d.balanca?.id ?? '',
      balancaUmidade: d.balancaUmidade?.id ?? '' }));
  }, [d.prensa?.id, d.balanca?.id, d.balancaUmidade?.id]);
  const prensas = equipamentos.filter((e) =>
    e.categoria === 'cisalhamento_direto' || e.categoria === 'multiprensa');

  return (
    <section className="bloco">
      <h2>Ficha do ensaio</h2>
      <div className="grade-campos fe-campos">
        <EscolhaPorCodigo rotulo="Prensa de cisalhamento"
          opcoes={prensas.map((p) => ({ valor: p.id, codigo: p.codigo_patrimonial,
            detalhe: p.nome }))}
          valor={f.prensa} aoEscolher={(v) => set('prensa', v)}
          erro={campoErro('prensaEquipamentoId')}
          dica="Entra na rastreabilidade do ensaio — e o deslocamento dela, também." />
        <EscolhaDeBalanca rotulo="Balança das massas do anel com solo"
          equipamentos={equipamentos} valor={f.balanca}
          aoEscolher={(v) => set('balanca', v)}
          erro={campoErro('balancaEquipamentoId')} />
        <EscolhaDeBalanca rotulo="Balança das cápsulas de umidade"
          equipamentos={equipamentos} valor={f.balancaUmidade}
          aoEscolher={(v) => set('balancaUmidade', v)}
          erro={campoErro('balancaUmidadeEquipamentoId')} />
      </div>
      <div className="grade-campos">
        <Escolha rotulo="Condição" opcoes={CONDICOES} valor={f.condicao}
          aoEscolher={(v) => set('condicao', v ?? f.condicao)} limpavel={false} />
        <Escolha rotulo="Tipo de amostra" opcoes={TIPOS_AMOSTRA} valor={f.tipoAmostra}
          aoEscolher={(v) => set('tipoAmostra', v)} />
        <Campo rotulo="Energia de compactação" value={f.energiaCompactacao}
          placeholder="Proctor normal"
          onChange={(e) => set('energiaCompactacao', e.target.value)} />
        <CampoNumerico rotulo="Tempo de saturação (h)" value={f.tempoSaturacaoH}
          onChange={(e) => set('tempoSaturacaoH', e.target.value)} />
        <CampoNumerico rotulo="Massa específica dos grãos ρs (g/cm³)"
          value={f.massaEspecificaGraos} erro={campoErro('massaEspecificaGraos')}
          dica="Sem ela não há índice de vazios nem grau de saturação."
          onChange={(e) => set('massaEspecificaGraos', e.target.value)} />
        <label className="campo campo-marca">
          <input type="checkbox" checked={f.graosAssumida}
            onChange={(e) => set('graosAssumida', e.target.checked)} />
          <span>
            ρs <b>assumida</b>, e não medida — o laudo diz isso ao lado do
            número.
          </span>
        </label>
        <CampoNumerico rotulo="Área configurada na prensa (mm²)"
          value={f.areaConfiguradaMm2}
          dica="A seção com que a prensa calcula a σ que grava. N = σ_prensa·A_configurada; o sistema divide N pela área do anel calibrado."
          onChange={(e) => set('areaConfiguradaMm2', e.target.value)} />
        <CampoNumerico rotulo="d_f — deslocamento estimado até a ruptura (mm)"
          value={f.deslocamentoRupturaEstimadoMm}
          erro={campoErro('deslocamentoRupturaEstimadoMm')}
          dica="ASTM D3080 9.10.4, sem experiência anterior: 10 mm para solo fino normalmente ou levemente sobreadensado; 5 mm para os demais. É o numerador da velocidade máxima."
          onChange={(e) => set('deslocamentoRupturaEstimadoMm', e.target.value)} />
      </div>

      <h3>Critério de ruptura — padrão deste ensaio</h3>
      <p className="explicacao">
        O sistema calcula os quatro critérios em todos os corpos de prova; o
        que vale para o resultado é o que você declarar aqui — ou o de cada
        CP, que tem precedência. Na etapa «Interpretação» os pontos de cada
        critério aparecem em cima da curva.
      </p>
      <div className="grade-campos">
        <Escolha rotulo="Critério" opcoes={CRITERIOS} valor={f.criterioRuptura}
          aoEscolher={(v) => set('criterioRuptura', v)} />
        <Campo rotulo="Referência do critério" value={f.criterioRupturaFonte}
          placeholder="ASTM D3080/D3080M-11, 3.2.1"
          dica="Vai impressa no laudo: quem lê precisa saber sob qual regra o ângulo saiu."
          onChange={(e) => set('criterioRupturaFonte', e.target.value)} />
        <CampoNumerico rotulo="Deslocamento limite (mm)"
          value={f.deslocamentoLimiteMm} erro={campoErro('deslocamentoLimiteMm')}
          dica="Do critério «τ no deslocamento declarado». Sem ele, esse critério não produz ponto."
          onChange={(e) => set('deslocamentoLimiteMm', e.target.value)} />
      </div>

      <Area rotulo="Observações do ensaio" rows={2} value={f.observacoes}
        dica="O que não se deduz de dado nenhum: pedregulho no plano de ruptura, uma paralisação, anel travado. O sistema não escreve isso por você."
        onChange={(e) => set('observacoes', e.target.value)} />

      <button type="button" className="primario"
        disabled={salvando === 'ficha'}
        onClick={() => gravar('ficha', () => gravarCisalhamentoDiretoFicha(id, {
          condicao: f.condicao,
          tipoAmostra: f.tipoAmostra,
          energiaCompactacao: f.energiaCompactacao || null,
          tempoSaturacaoH: paraNumero(f.tempoSaturacaoH) ?? null,
          massaEspecificaGraos: paraNumero(f.massaEspecificaGraos) ?? null,
          graosAssumida: f.graosAssumida,
          areaConfiguradaMm2: paraNumero(f.areaConfiguradaMm2) ?? null,
          deslocamentoRupturaEstimadoMm: paraNumero(f.deslocamentoRupturaEstimadoMm) ?? null,
          deslocamentoLimiteMm: paraNumero(f.deslocamentoLimiteMm) ?? null,
          criterioRuptura: f.criterioRuptura,
          criterioRupturaFonte: f.criterioRupturaFonte || null,
          observacoes: f.observacoes || null,
          prensaEquipamentoId: f.prensa || null,
          balancaEquipamentoId: f.balanca || undefined,
          balancaUmidadeEquipamentoId: f.balancaUmidade || undefined,
        }))}>
        {salvando === 'ficha' ? 'Gravando…' : 'Gravar a ficha'}
      </button>
    </section>
  );
}

function DadosDoCp({ cp, cab, gravar, salvando, campoErro, equipamentos }: {
  cp: CorpoDeProvaCd;
  cab: any;
  gravar: Gravar;
  salvando: string | null;
  campoErro(c: string): string | undefined;
  equipamentos: EquipamentoDaLista[];
}) {
  const c = cp.cp;
  const deCp = () => ({
    identificacao: c.identificacao ?? '',
    anel: c.anel_equipamento_id ?? '',
    tensaoNormalKpa: paraCampo(c.tensao_normal_kpa),
    massaAnelSoloUmidoG: paraCampo(c.massa_anel_solo_umido_g),
    massaAnelSoloSaturadoG: paraCampo(c.massa_anel_solo_saturado_g),
    velocidadeInformadaMmMin: paraCampo(c.velocidade_informada_mm_min),
    numeroTesteEquipamento: paraCampo(c.numero_teste_equipamento),
    dataEnsaio: (c.data_ensaio ?? '').slice(0, 10),
    situacao: c.situacao as string,
    motivoDescarte: c.motivo_descarte ?? '',
    observacoes: c.observacoes ?? '',
  });
  const [f, setF] = useState(deCp);
  /* Trocar de corpo de prova troca o formulário inteiro. */
  useEffect(() => { setF(deCp()); }, [c.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k: string, v: unknown) => setF((x) => ({ ...x, [k]: v }));
  const aneis = equipamentos.filter((e) => e.categoria === 'anel_cisalhamento');

  return (
    <section className="bloco">
      <h2>Corpo de prova {c.identificacao}</h2>
      <div className="grade-campos">
        <Campo rotulo="Identificação" value={f.identificacao}
          onChange={(e) => set('identificacao', e.target.value)} />
        <EscolhaPorCodigo rotulo="Anel de cisalhamento"
          opcoes={aneis.map((a) => ({ valor: a.id, codigo: a.codigo_patrimonial,
            detalhe: a.nome }))}
          valor={f.anel} aoEscolher={(v) => set('anel', v)}
          erro={campoErro('anelEquipamentoId')}
          dica="A área, o lado, a altura, o volume e a massa vêm da calibração interna vigente do anel — quadrado (RN-224). Não se digitam." />
        <CampoNumerico rotulo="Tensão normal declarada σ (kPa)"
          value={f.tensaoNormalKpa}
          onChange={(e) => set('tensaoNormalKpa', e.target.value)} />
        <CampoNumerico rotulo="Anel + solo úmido (g)" value={f.massaAnelSoloUmidoG}
          dica="Na moldagem. Menos a massa do anel, sobre o volume do anel: γ natural."
          onChange={(e) => set('massaAnelSoloUmidoG', e.target.value)} />
        <CampoNumerico rotulo="Anel + solo saturado (g)" value={f.massaAnelSoloSaturadoG}
          dica={cab.condicao === 'inundado' ? 'Depois da inundação: γ saturada.' : 'Se houver.'}
          onChange={(e) => set('massaAnelSoloSaturadoG', e.target.value)} />
        <CampoNumerico rotulo="Velocidade informada na prensa (mm/min)"
          value={f.velocidadeInformadaMmMin}
          dica="A que se programou. A real (δh/t_e) sai das leituras, e as duas têm de bater."
          onChange={(e) => set('velocidadeInformadaMmMin', e.target.value)} />
        <CampoNumerico rotulo="Nº do teste na prensa" inteiro
          value={f.numeroTesteEquipamento}
          onChange={(e) => set('numeroTesteEquipamento', e.target.value)} />
        <Campo rotulo="Data do ensaio" type="date" value={f.dataEnsaio}
          dica="É a data em que o anel tem de estar calibrado."
          onChange={(e) => set('dataEnsaio', e.target.value)} />
      </div>

      <div className="resumo-fase">
        <span>Anel <b>{c.anel ?? '—'}</b></span>
        <span>L {n(c.lado_mm, 2)} mm</span>
        <span>H₀ {n(c.altura_mm, 2)} mm</span>
        <span>A {n(c.area_mm2, 1)} mm²</span>
        <span>V {n(c.volume_cm3, 3)} cm³</span>
      </div>

      <div className="grade-campos">
        <Escolha rotulo="Situação" opcoes={SITUACOES} valor={f.situacao}
          aoEscolher={(v) => set('situacao', v ?? f.situacao)} limpavel={false} />
        {f.situacao === 'descartado' && (
          <Campo rotulo="Motivo do descarte" value={f.motivoDescarte}
            erro={campoErro('motivoDescarte')}
            onChange={(e) => set('motivoDescarte', e.target.value)} />
        )}
        <Area rotulo="Observações do corpo de prova" rows={2} value={f.observacoes}
          onChange={(e) => set('observacoes', e.target.value)} />
      </div>

      <button type="button" className="primario" disabled={salvando === 'cp'}
        onClick={() => gravar('cp', () => editarCisalhamentoDiretoCp(c.id, {
          identificacao: f.identificacao,
          anelEquipamentoId: f.anel || null,
          tensaoNormalKpa: paraNumero(f.tensaoNormalKpa) ?? null,
          massaAnelSoloUmidoG: paraNumero(f.massaAnelSoloUmidoG) ?? null,
          massaAnelSoloSaturadoG: paraNumero(f.massaAnelSoloSaturadoG) ?? null,
          velocidadeInformadaMmMin: paraNumero(f.velocidadeInformadaMmMin) ?? null,
          numeroTesteEquipamento: paraNumero(f.numeroTesteEquipamento) ?? null,
          dataEnsaio: f.dataEnsaio || null,
          situacao: f.situacao,
          motivoDescarte: f.motivoDescarte || null,
          observacoes: f.observacoes || null,
        }))}>
        {salvando === 'cp' ? 'Gravando…' : 'Gravar o corpo de prova'}
      </button>
    </section>
  );
}

// ── As cápsulas de umidade ──────────────────────────────────────────

/*
 * As cápsulas de umidade — gravadas em cima, novas embaixo (onda 294).
 *
 * Até a 0246 as linhas gravadas voltavam editáveis e o «Gravar» reenviava
 * todas: o upsert reescrevia a pesagem sem perguntar. Agora a linha
 * gravada trava como nos triviais (RN-236): a massa úmida aparece como
 * texto, a seca pesa na própria coluna enquanto a cápsula está na estufa
 * (RN-241), e a correção é pelo «Corrigir». Só a linha nova sobe.
 */
interface CapsulaNova {
  ordem: number;
  capsulaEquipamentoId: string;
  umido: string;
  seco: string;
}

function Capsulas({ id, cpId, etapa, linhas, capsulas, gravar, salvando, titulo,
  explicacao, estufa, congelado, recarregar }: {
  id: string;
  cpId: string | null;
  etapa: 'inicial' | 'final';
  linhas: any[];
  capsulas: EquipamentoDaLista[];
  gravar: Gravar;
  salvando: string | null;
  titulo: string;
  explicacao: string;
  estufa: Estufa;
  congelado: boolean;
  recarregar(): Promise<void>;
}) {
  const [descartando, setDescartando] = useState<any | null>(null);
  const [novas, setNovas] = useState<CapsulaNova[]>([]);
  const marca = `caps-${cpId ?? 'comum'}-${etapa}`;
  const set = (i: number, k: keyof CapsulaNova, v: string) => setNovas(
    (x) => x.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const proxima = () => Math.max(
    linhas.reduce((m: number, u: any) => Math.max(m, u.ordem), 0),
    novas.reduce((m, l) => Math.max(m, l.ordem), 0)) + 1;
  const opcoesCapsula = capsulas.map((o) => ({ valor: o.id, texto: o.codigo_patrimonial }));
  /* A seca é opcional: sem ela, a cápsula vai para a estufa (RN-241). */
  const prontas = novas.filter((l) => l.capsulaEquipamentoId
    && paraNumero(l.umido) !== undefined);
  const naEstufa = linhas.filter(naEstufaDaFicha).length;

  const lancadas = linhas.map((u: any) => ({
    chave: `${marca}-${u.id}`,
    classe: u.descartada ? 'descartada' : undefined,
    titulo: `Cápsula ${u.ordem}`,
    resultado: u.descartada ? '—' : naEstufaDaFicha(u) ? 'na estufa' : `${n(u.umidade, 2)} %`,
    semCartao: [0, 5],
    celulas: [
      u.ordem,
      <span key="c" className="cod">{u.capsula ?? '—'}</span>,
      n(u.tara, 3),
      n(u.massa_capsula_solo_umido, 2),
      <SecaDaLinha key="s" linha={u} estufa={estufa} congelado={congelado}
        opcoesCapsula={opcoesCapsula} aoMudou={recarregar} />,
      <WDaLinha key="w" linha={u} />,
      !u.descartada ? (
        <button key="d" type="button" className="botao discreto pequeno fe-descartar"
          aria-label={`Descartar a cápsula ${u.ordem}`}
          onClick={() => setDescartando(u)}>Descartar</button>
      ) : <span key="d" className="nada">descartada — {u.motivo_descarte}</span>,
    ],
  }));

  const emBranco = novas.map((l, i) => ({
    chave: `${marca}-nova-${l.ordem}`,
    classe: 'nova',
    titulo: `Cápsula ${l.ordem} · nova`,
    resultado: '—',
    semCartao: [0, 5],
    celulas: [
      l.ordem,
      <select key="c" value={l.capsulaEquipamentoId} aria-label="Cápsula"
        onChange={(e) => set(i, 'capsulaEquipamentoId', e.target.value)}>
        <option value="">Escolha…</option>
        {capsulas.map((o) => (
          <option key={o.id} value={o.id}>{o.codigo_patrimonial}</option>
        ))}
      </select>,
      '—',
      <CampoNumerico key="u" rotulo="Cáps. + solo úmido (g)" rotuloOculto
        value={l.umido} onChange={(e) => set(i, 'umido', e.target.value)} />,
      <CampoNumerico key="s" rotulo="Cáps. + solo seco (g)" rotuloOculto
        placeholder="ou na estufa"
        value={l.seco} onChange={(e) => set(i, 'seco', e.target.value)} />,
      '—',
      <button key="r" type="button" className="remover" tabIndex={-1}
        aria-label={`Remover a cápsula ${l.ordem}`}
        title="Remover esta linha (ainda não gravada)"
        onClick={() => setNovas((x) => x.filter((_, j) => j !== i))}>
        ×
      </button>,
    ],
  }));

  return (
    <section className="bloco">
      <h2>{titulo}</h2>
      <p className="explicacao">{explicacao}</p>
      {linhas.length === 0 && novas.length === 0 ? (
        <Vazio>Nenhuma cápsula lançada.</Vazio>
      ) : (
        <TabelaDeLancamento
          rotulo={titulo}
          colunas={[
            { texto: 'Nº', num: true },
            { texto: 'Cápsula' },
            { texto: 'Tara (g)', num: true },
            { texto: 'Cáps. + solo úmido (g)', num: true },
            { texto: 'Cáps. + solo seco (g)', num: true },
            { texto: 'w (%)', num: true },
            { texto: '' },
          ]}
          linhas={[...lancadas, ...emBranco]} />
      )}
      {!congelado && (linhas.length > 0 || novas.length > 0)
        && <LembreteDaEstufa quantas={naEstufa} />}
      <div className="acoes">
        <button type="button"
          onClick={() => setNovas((x) => [...x, {
            ordem: proxima(), capsulaEquipamentoId: '', umido: '', seco: '',
          }])}>
          + Cápsula
        </button>
        <button type="button" className="primario" data-gravar-tabela=""
          disabled={salvando === marca || !prontas.length}
          onClick={async () => {
            const feito = await gravar(marca, () => gravarCisalhamentoDiretoUmidade(id, {
              cpId, etapa,
              capsulas: prontas.map((l) => ({
                ordem: l.ordem,
                capsulaEquipamentoId: l.capsulaEquipamentoId,
                massaCapsulaSoloUmido: paraNumero(l.umido),
                ...(paraNumero(l.seco) !== undefined
                  ? { massaCapsulaSoloSeco: paraNumero(l.seco) } : {}),
              })),
            }));
            if (feito) setNovas((x) => x.filter((l) => !prontas.includes(l)));
          }}>
          {salvando === marca ? 'Gravando…' : 'Gravar as cápsulas'}
        </button>
      </div>
      <JanelaDeDescarte
        alvo={descartando ? `a cápsula ${descartando.ordem}` : null}
        aoFechar={() => setDescartando(null)}
        aoConfirmar={(motivo) => gravar(marca,
          () => descartarCisalhamentoDiretoCapsula(descartando.id, motivo))}
        salvando={salvando !== null}
      />
    </section>
  );
}

function Indices({ cp }: { cp: any }) {
  const item = (rot: string, val: string) => (
    <div className="indice" key={rot}><dt>{rot}</dt><dd>{val}</dd></div>
  );
  return (
    <section className="bloco">
      <h2>Índices físicos</h2>
      <dl className="grade-indices">
        {item('w₀ (%)', n(cp.umidade_inicial_pct, 2))}
        {item('γ natural (g/cm³)', n(cp.massa_especifica_natural, 4))}
        {item('γ saturada (g/cm³)', n(cp.massa_especifica_saturada, 4))}
        {item('γd (g/cm³)', n(cp.massa_especifica_seca, 4))}
        {item('e₀', n(cp.indice_vazios_inicial, 4))}
        {item('Sr₀ (%)', n(cp.grau_saturacao_inicial_pct, 1))}
        {item('w_f (%)', n(cp.umidade_final_pct, 2))}
        {item('Altura após adensar (mm)', n(cp.altura_apos_adensamento_mm, 3))}
      </dl>
      <p className="explicacao">
        {cp.umidade_inicial_origem === 'comum'
          ? 'w₀ das cápsulas comuns do ensaio. '
          : cp.umidade_inicial_origem === 'do_cp' ? 'w₀ das cápsulas deste CP. ' : ''}
        γ = (anel + solo − anel)/V do anel · γd = γ/(1 + w₀/100) · e₀ =
        ρs/γd − 1 · Sr₀ = w₀·ρs/e₀. Nenhum é digitado nem calculado aqui:
        eles vêm do banco.
      </p>
    </section>
  );
}

// ── A importação da planilha da prensa ──────────────────────────────

function ImportarPlanilha({ d, id, recarregar }: {
  d: EnsaioCisalhamentoDireto; id: string; recarregar(): Promise<void>;
}) {
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [conf, setConf] = useState<ConferenciaCd | null>(null);
  const [mapa, setMapa] = useState<Record<string, string>>({});
  const [substituir, setSubstituir] = useState(false);
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  const vivos = d.corpos.filter((c: any) => c.situacao !== 'descartado');

  async function rodar(simular: boolean) {
    if (!arquivo) return;
    setOcupado(true); setErro(null);
    try {
      const r = await importarPlanilhaCisalhamentoDireto(id, arquivo,
        { simular, substituir, mapa });
      setConf(r);
      if (simular) {
        const m: Record<string, string> = {};
        for (const t of r.testes) if (t.corpoDeProva) m[String(t.teste)] = t.corpoDeProva.id;
        setMapa((x) => ({ ...m, ...x }));
      } else {
        await recarregar();
      }
    } catch (e) { setErro(e); } finally { setOcupado(false); }
  }

  return (
    <section className="bloco">
      <h2>Planilha da prensa</h2>
      <p className="explicacao">
        A planilha do relatório (modelo Owntec da MS 101), com as abas «G -
        Pré-Adens. - Teste n» e «J - Cisalh. Direto - Teste n». Cada teste
        vai ao CP de mesma ordem — ou ao que você escolher abaixo. O arquivo
        original fica guardado, imutável; as colunas calculadas pela
        planilha não se leem, porque a conta é do banco.
      </p>
      <ZonaDeArquivo rotulo="Planilha do ensaio" tipos="XLSX"
        aceita=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        arquivos={arquivo} desabilitado={ocupado}
        aoEscolher={(f) => { setArquivo(f[0] ?? null); setConf(null); setMapa({}); }} />

      {erro ? <ErroDaApi erro={erro} /> : null}

      {conf && (
        <div className="conferencia">
          <h3>O que a planilha tem</h3>
          <p>
            <b>{conf.arquivo.nome}</b>
            {conf.arquivo.formaCp ? ` · CP ${String(conf.arquivo.formaCp).toLowerCase()}` : ''}
            {conf.arquivo.areaMm2 ? ` · área configurada ${n(conf.arquivo.areaMm2, 0)} mm²` : ''}
          </p>
          <div className="rolar-lado"><table className="dados">
            <thead><tr>
              <th className="num">Teste</th><th className="num">Nº na prensa</th>
              <th>Data</th><th className="num">σ (kPa)</th>
              <th className="num">Adensamento</th><th className="num">Cisalhamento</th>
              <th className="num">δh máx. (mm)</th><th>Vai para</th><th>Avisos</th>
            </tr></thead>
            <tbody>
              {conf.testes.map((t) => (
                <tr key={t.teste}>
                  <td className="num">{t.teste}</td>
                  <td className="num">{t.numeroTeste ?? '—'}</td>
                  <td>{t.dataEnsaio ? t.dataEnsaio.split('-').reverse().join('/') : '—'}</td>
                  <td className="num">{n(t.tensaoNormalKpa, 2)}</td>
                  <td className="num">{t.adensamento}</td>
                  <td className="num">{t.cisalhamento}</td>
                  <td className="num">{n(t.deslocamentoHorizontalMaxMm, 3)}</td>
                  <td>
                    <select aria-label={`CP do teste ${t.teste}`}
                      value={mapa[String(t.teste)] ?? ''}
                      onChange={(e) => setMapa((m) => ({ ...m, [String(t.teste)]: e.target.value }))}>
                      <option value="">Escolha…</option>
                      {vivos.map((c: any) => (
                        <option key={c.id} value={c.id}>{c.identificacao}</option>
                      ))}
                    </select>
                  </td>
                  <td><small>{t.avisos.join('; ') || '—'}</small></td>
                </tr>
              ))}
            </tbody>
          </table></div>
          {conf.avisos.length > 0 && (
            <Aviso tipo="atencao" titulo="O leitor avisou">
              <ul>{conf.avisos.map((a, i) => <li key={i}>{a}</li>)}</ul>
            </Aviso>
          )}
          {conf.precisaSubstituir && !conf.gravado && (
            <label className="campo campo-marca">
              <input type="checkbox" checked={substituir}
                onChange={(e) => setSubstituir(e.target.checked)} />
              <span>
                Substituir as leituras que já existem — as atuais ficam
                <b> descartadas</b>, com o motivo; nada se apaga.
              </span>
            </label>
          )}
          {conf.gravado && (
            <Aviso tipo="ok" titulo="Importado">
              {conf.gravado.testes} teste(s) e {conf.gravado.leituras} leitura(s).
            </Aviso>
          )}
        </div>
      )}

      {d.arquivos.length > 0 && (
        <p className="explicacao">
          Já importado: {d.arquivos.map((a: any) => a.nome_original).join(', ')}.
        </p>
      )}

      <div className="acoes">
        <button type="button" disabled={!arquivo || ocupado} onClick={() => rodar(true)}>
          {ocupado ? 'Lendo…' : 'Conferir sem gravar'}
        </button>
        <button type="button" className="primario"
          disabled={!arquivo || ocupado || !conf || !!conf.gravado
            || (conf.precisaSubstituir && !substituir)}
          onClick={() => rodar(false)}>
          Importar
        </button>
      </div>
    </section>
  );
}

// ── Colar leituras da planilha ──────────────────────────────────────

/**
 * Lê o que se colou de uma planilha: uma leitura por linha, colunas
 * separadas por tabulação (o Excel copia assim) ou ponto e vírgula, com
 * vírgula decimal. Linha que não tem os números obrigatórios — o título,
 * uma linha vazia — é ignorada e contada.
 */
function lerColagem(texto: string, etapa: 'adensamento' | 'cisalhamento') {
  const linhas: any[] = [];
  let ignoradas = 0;
  for (const bruta of texto.split(/\r?\n/)) {
    const l = bruta.trim();
    if (!l) continue;
    let cel = l.split(/\t|;/).map((s) => s.trim());
    if (cel.length === 1) cel = l.split(/\s+/);
    const v = cel.map((c) => paraNumero(c));
    if (etapa === 'adensamento') {
      if (v[0] === undefined || v[1] === undefined) { ignoradas += 1; continue; }
      linhas.push({ tempoS: v[0], deslocamentoVerticalMm: v[1],
        tensaoNormalKpa: v[2] ?? null });
    } else {
      if (v[1] === undefined || v[2] === undefined) { ignoradas += 1; continue; }
      linhas.push({ tempoS: v[0] ?? null, forcaKgf: v[1], deslocamentoHorizontalMm: v[2],
        deslocamentoVerticalMm: v[3] ?? null, tensaoNormalKpa: v[4] ?? null });
    }
  }
  return { linhas, ignoradas };
}

function ColarLeituras({ cp, etapa, gravar, salvando }: {
  cp: CorpoDeProvaCd; etapa: 'adensamento' | 'cisalhamento';
  gravar: Gravar; salvando: string | null;
}) {
  const [texto, setTexto] = useState('');
  const [substituir, setSubstituir] = useState(false);
  useEffect(() => { setTexto(''); setSubstituir(false); }, [cp.cp.id]);
  const lido = useMemo(() => lerColagem(texto, etapa), [texto, etapa]);
  const atuais = (etapa === 'adensamento' ? cp.adensamento : cp.cisalhamento)
    .filter((l: any) => !l.descartada).length;
  const marca = `colar-${etapa}`;
  return (
    <details className="bloco colar-leituras">
      <summary><b>Colar da planilha</b> — sem o arquivo da prensa</summary>
      <p className="explicacao">
        {etapa === 'adensamento'
          ? 'Uma leitura por linha, nesta ordem: tempo decorrido (s), deslocamento vertical (mm) e, se houver, tensão normal (kPa).'
          : 'Uma leitura por linha, nesta ordem: tempo (s), força (kgf), deslocamento horizontal (mm), deslocamento vertical (mm) e tensão normal da prensa (kPa) — as duas últimas podem ficar vazias.'}
        {' '}Copie as colunas do Excel e cole aqui; o título é ignorado.
      </p>
      <textarea aria-label={`Leituras de ${etapa} coladas`} value={texto}
        onChange={(e) => setTexto(e.target.value)} rows={8} spellCheck={false} />
      <p className="resumo-fase">
        <span>{plural(lido.linhas.length, 'leitura lida', 'leituras lidas')}</span>
        {lido.ignoradas > 0 && <span>{plural(lido.ignoradas, 'linha ignorada', 'linhas ignoradas')}</span>}
      </p>
      {atuais > 0 && (
        <label className="campo campo-marca">
          <input type="checkbox" checked={substituir}
            onChange={(e) => setSubstituir(e.target.checked)} />
          <span>
            Substituir as {atuais} leituras atuais — elas ficam
            <b> descartadas</b>, com o motivo.
          </span>
        </label>
      )}
      <button type="button" className="primario"
        disabled={!lido.linhas.length || salvando === marca || (atuais > 0 && !substituir)}
        onClick={async () => {
          const okk = await gravar(marca, () => colarLeiturasCisalhamentoDireto(cp.cp.id, {
            etapa, substituir: atuais > 0 ? substituir : undefined, leituras: lido.linhas,
          }));
          if (okk) setTexto('');
        }}>
        {salvando === marca ? 'Gravando…' : `Gravar ${plural(lido.linhas.length, 'leitura', 'leituras')}`}
      </button>
    </details>
  );
}

/** Marcar leituras e descartá-las com motivo — nada se apaga. */
function useDescarteDeLeituras(cp: CorpoDeProvaCd, etapa: 'adensamento' | 'cisalhamento',
  gravar: Gravar) {
  const [marcadas, setMarcadas] = useState<number[]>([]);
  const [aberto, setAberto] = useState(false);
  const [verDescartadas, setVerDescartadas] = useState(false);
  useEffect(() => { setMarcadas([]); }, [cp.cp.id, cp.adensamento, cp.cisalhamento]);
  const linhas = etapa === 'adensamento' ? cp.adensamento : cp.cisalhamento;
  const nDescartadas = linhas.filter((l: any) => l.descartada).length;
  const visiveis = verDescartadas ? linhas : linhas.filter((l: any) => !l.descartada);
  const alternar = (o: number) => setMarcadas((m) =>
    (m.includes(o) ? m.filter((x) => x !== o) : [...m, o]));
  const janela = (salvando: string | null) => (
    <JanelaDeDescarte
      alvo={aberto ? plural(marcadas.length, 'leitura marcada', 'leituras marcadas') : null}
      aoFechar={() => setAberto(false)}
      aoConfirmar={(motivo) => gravar(`descartar-${etapa}`,
        () => descartarLeiturasCisalhamentoDireto(cp.cp.id,
          { etapa, ordens: marcadas, motivo }))}
      salvando={salvando !== null} />
  );
  /* As descartadas ficam fora da tabela até alguém pedir: nada se apaga,
     mas uma substituição dobraria a tabela com linhas riscadas. */
  const botao = (
    <>
      <button type="button" className="botao discreto" disabled={!marcadas.length}
        onClick={() => setAberto(true)}>
        Descartar {plural(marcadas.length, 'marcada', 'marcadas')}
      </button>
      {nDescartadas > 0 && (
        <label className="opcao-marcar">
          <input type="checkbox" checked={verDescartadas}
            onChange={(e) => setVerDescartadas(e.target.checked)} />
          mostrar {plural(nDescartadas, 'leitura descartada', 'leituras descartadas')}
        </label>
      )}
    </>
  );
  return { marcadas, alternar, janela, botao, visiveis };
}

// ══ Etapa 2 · o adensamento ═══════════════════════════════════════════

function FaseAdensamento({ cp, gravar, salvando }: {
  cp: CorpoDeProvaCd; gravar: Gravar; salvando: string | null;
}) {
  const desc = useDescarteDeLeituras(cp, 'adensamento', gravar);
  const vivas = cp.adensamento.filter((a: any) => !a.descartada);
  const ult = vivas[vivas.length - 1];
  return (
    <section className="bloco">
      <h2>Adensamento — {cp.cp.identificacao}</h2>
      <p className="explicacao">
        As leituras do pré-adensamento sob a tensão normal. A curva e a
        construção do tempo até a falha (t₉₀ e t₁₀₀) estão na etapa
        «Interpretação».
      </p>
      {!vivas.length ? (
        <Vazio>
          Nenhuma leitura. Importe a planilha da prensa na etapa «Amostra»,
          ou cole as leituras abaixo.
        </Vazio>
      ) : (
        <div className="resumo-fase">
          <span>{plural(vivas.length, 'leitura', 'leituras')}</span>
          <span>tempo final {n(ult?.tempo_min, 2)} min</span>
          <span>δv ao fim {n(cp.cp.deslocamento_vertical_adensamento_mm, 3)} mm</span>
          <span>altura após adensar {n(cp.cp.altura_apos_adensamento_mm, 3)} mm</span>
        </div>
      )}
      <ColarLeituras cp={cp} etapa="adensamento" gravar={gravar} salvando={salvando} />
      {cp.adensamento.length > 0 && (
        <>
          <div className="acoes">{desc.botao}</div>
          <div className="rolar-lado tabela-leituras"><table className="dados">
            <thead><tr>
              <th />
              <th className="num">Nº</th><th className="num">t (s)</th>
              <th className="num">t (min)</th><th className="num">√t (min^½)</th>
              <th className="num">δv (mm)</th><th className="num">Altura (mm)</th>
              <th className="num">σ prensa (kPa)</th><th>Origem</th>
            </tr></thead>
            <tbody>
              {desc.visiveis.map((a: any) => (
                <tr key={a.id} className={a.descartada ? 'descartada' : ''}
                  title={a.descartada ? `descartada — ${a.motivo_descarte}` : undefined}>
                  <td>{!a.descartada && (
                    <input type="checkbox" aria-label={`Marcar a leitura ${a.ordem}`}
                      checked={desc.marcadas.includes(Number(a.ordem))}
                      onChange={() => desc.alternar(Number(a.ordem))} />)}</td>
                  <td className="num">{a.ordem}</td>
                  <td className="num">{n(a.tempo_s, 1, true)}</td>
                  <td className="num">{n(a.tempo_min, 3)}</td>
                  <td className="num">{n(a.raiz_tempo_min, 3)}</td>
                  <td className="num">{n(a.deslocamento_vertical_mm, 4)}</td>
                  <td className="num">{n(a.altura_mm, 3)}</td>
                  <td className="num">{n(a.tensao_normal_kpa, 2)}</td>
                  <td>{rotulo(a.origem)}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </>
      )}
      {desc.janela(salvando)}
    </section>
  );
}

// ══ Etapa 3 · o cisalhamento ══════════════════════════════════════════

function FaseCisalhamento({ cp, gravar, salvando }: {
  cp: CorpoDeProvaCd; gravar: Gravar; salvando: string | null;
}) {
  const desc = useDescarteDeLeituras(cp, 'cisalhamento', gravar);
  const c = cp.cp;
  const v = cp.velocidade;
  const vivas = cp.cisalhamento.filter((l: any) => !l.descartada);
  return (
    <section className="bloco">
      <h2>Cisalhamento — {c.identificacao}</h2>
      <p className="explicacao">
        τ = F·g/A e σ = N/A sobre a área do anel calibrado (nominal, ASTM
        D3080); a corrigida divide os dois pela área de contato A·(1 − δh/L).
        As curvas e a escolha do ponto de ruptura estão na etapa
        «Interpretação».
      </p>
      {!vivas.length ? (
        <Vazio>
          Nenhuma leitura. Importe a planilha da prensa na etapa «Amostra»,
          ou cole as leituras abaixo.
        </Vazio>
      ) : (
        <div className="resumo-fase">
          <span>{plural(vivas.length, 'leitura', 'leituras')}</span>
          <span>δh máximo {n(c.deslocamento_horizontal_max_mm, 3)} mm
            ({n(c.deslocamento_relativo_max_pct, 1)} % de L)</span>
          <span>σ nominal média {n(c.tensao_normal_nominal_kpa, 2)} kPa</span>
          <span>V real {n(c.velocidade_calculada_mm_min, 4)} mm/min</span>
          <span>V informada {n(c.velocidade_informada_mm_min, 4)} mm/min</span>
          {v?.divergencia_pct != null && Number(v.divergencia_pct) > 5 && (
            <Selo tom="atencao">Diferem {n(v.divergencia_pct, 1)} %</Selo>
          )}
        </div>
      )}
      <ColarLeituras cp={cp} etapa="cisalhamento" gravar={gravar} salvando={salvando} />
      {cp.cisalhamento.length > 0 && (
        <>
          <div className="acoes">{desc.botao}</div>
          <div className="rolar-lado tabela-leituras"><table className="dados">
            <thead><tr>
              <th />
              <th className="num">Nº</th><th className="num">t (min)</th>
              <th className="num">F (kgf)</th><th className="num">δh (mm)</th>
              <th className="num">δh/L (%)</th><th className="num">δv (mm)</th>
              <th className="num">σ (kPa)</th><th className="num">τ (kPa)</th>
              <th className="num">A_c (mm²)</th><th className="num">σ_c (kPa)</th>
              <th className="num">τ_c (kPa)</th>
            </tr></thead>
            <tbody>
              {desc.visiveis.map((l: any) => (
                <tr key={l.id} className={l.descartada ? 'descartada' : ''}
                  title={l.descartada ? `descartada — ${l.motivo_descarte}` : undefined}>
                  <td>{!l.descartada && (
                    <input type="checkbox" aria-label={`Marcar a leitura ${l.ordem}`}
                      checked={desc.marcadas.includes(Number(l.ordem))}
                      onChange={() => desc.alternar(Number(l.ordem))} />)}</td>
                  <td className="num">{l.ordem}</td>
                  <td className="num">{n(l.tempo_min, 3)}</td>
                  <td className="num">{n(l.forca_kgf, 3)}</td>
                  <td className="num">{n(l.deslocamento_horizontal_mm, 3)}</td>
                  <td className="num">{n(l.deslocamento_relativo_pct, 2)}</td>
                  <td className="num">{n(l.deslocamento_vertical_mm, 3)}</td>
                  <td className="num">{n(l.sigma_nominal_kpa, 2)}</td>
                  <td className="num forte">{n(l.tau_nominal_kpa, 2)}</td>
                  <td className="num">{n(l.area_corrigida_mm2, 1)}</td>
                  <td className="num">{n(l.sigma_corrigida_kpa, 2)}</td>
                  <td className="num">{n(l.tau_corrigida_kpa, 2)}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </>
      )}
      {desc.janela(salvando)}
    </section>
  );
}

// ══ Etapa 4 · a interpretação — onde moram os gráficos ════════════════

function FaseInterpretacao({ d, cp, cpId, id, gravar, salvando, versao }: {
  d: EnsaioCisalhamentoDireto;
  cp: CorpoDeProvaCd | null;
  cpId: string | null;
  id: string;
  gravar: Gravar;
  salvando: string | null;
  versao: number;
}) {
  const [base, setBase] = useState<'nominal' | 'corrigida'>('nominal');
  const [cps, setCps] = useState<CorpoDeProvaCd[]>([]);
  const chave = d.corpos.map((c: any) => c.id).join(',');
  useEffect(() => {
    let vivo = true;
    void Promise.all(d.corpos.map((c: any) => verCisalhamentoDiretoCp(c.id)))
      .then((r) => { if (vivo) setCps(r); }).catch(() => undefined);
    return () => { vivo = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chave, versao]);

  if (!d.corpos.length) {
    return <Vazio>Nenhum corpo de prova ainda. Crie os corpos de prova na etapa «Amostra».</Vazio>;
  }

  return (
    <>
      <section className="bloco">
        <h2>Tempo até a falha e velocidade de cisalhamento</h2>
        <p className="explicacao">
          A velocidade REAL é δh/t_e, da primeira à última leitura do
          cisalhamento (ASTM D3080, 10.2.3), e tem de bater com a informada
          na prensa. A MÁXIMA é d_f/t_f: pela ASTM D3080 t_f = 11,6·t₉₀
          (Taylor); pela BS 1377, t_f = 12,7·t₁₀₀. t₉₀ e t₁₀₀ saem do
          traçado que você faz na curva do adensamento, corpo de prova a
          corpo de prova.
        </p>
        {!cp ? <Carregando texto="Abrindo o corpo de prova" /> : !cp.adensamento.length ? (
          <Vazio>O {cp.cp.identificacao} não tem leituras de adensamento.</Vazio>
        ) : (
          <GraficoDoTracado cp={cp} gravando={salvando === 'tracado'}
            aoGravar={(t) => gravar('tracado', () => tracarCisalhamentoDireto(cp.cp.id, t))} />
        )}
        <QuadroDeVelocidades d={d} />
      </section>

      <section className="bloco">
        <h2>Cisalhamento e ruptura</h2>
        <Escolha rotulo="Tensões nos gráficos" opcoes={BASES} valor={base}
          aoEscolher={(v) => setBase((v as 'nominal' | 'corrigida') ?? 'nominal')}
          limpavel={false} />
        <GraficosDoCisalhamentoCd d={d} cps={cps} cpId={cpId} base={base}
          apontando={salvando === 'apontar'}
          aoApontar={(dh) => cpId && gravar('apontar', () => editarCisalhamentoDiretoCp(cpId, {
            criterioRuptura: 'manual', rupturaDeslocamentoMm: dh,
          }))} />
        {cp && <AnaliseDosCriterios cp={cp} cab={d.cabecalho} gravar={gravar} salvando={salvando} />}
      </section>

      <Envoltoria d={d} id={id} gravar={gravar} salvando={salvando} />
    </>
  );
}

function QuadroDeVelocidades({ d }: { d: EnsaioCisalhamentoDireto }) {
  const lista = (d.velocidades ?? []).filter((v: any) => v.situacao !== 'descartado');
  if (!lista.length) return null;
  const acima = (v: any) => {
    const real = v.velocidade_calculada_mm_min ?? v.velocidade_informada_mm_min;
    if (real == null) return false;
    return [v.velocidade_maxima_astm_mm_min, v.velocidade_maxima_bs_mm_min]
      .some((m) => m != null && Number(real) > Number(m));
  };
  return (
    <>
      <h3>Velocidades por corpo de prova</h3>
      <div className="rolar-lado"><table className="dados">
        <thead><tr>
          <th>CP</th><th className="num">V informada</th><th className="num">V real</th>
          <th className="num">Diferença (%)</th><th className="num">t₉₀ (min)</th>
          <th className="num">t_f ASTM (min)</th><th className="num">V máx. ASTM</th>
          <th className="num">t₁₀₀ (min)</th><th className="num">t_f BS (min)</th>
          <th className="num">V máx. BS</th><th>Situação</th>
        </tr></thead>
        <tbody>
          {lista.map((v: any) => (
            <tr key={v.cp_id}>
              <td>{v.identificacao}</td>
              <td className="num">{n(v.velocidade_informada_mm_min, 4)}</td>
              <td className="num forte">{n(v.velocidade_calculada_mm_min, 4)}</td>
              <td className="num">{n(v.divergencia_pct, 1)}</td>
              <td className="num" title={v.motivo_sem_t90 ?? undefined}>{n(v.t90_min, 2)}</td>
              <td className="num">{n(v.tf_astm_min, 1)}</td>
              <td className="num">{n(v.velocidade_maxima_astm_mm_min, 4)}</td>
              <td className="num" title={v.motivo_sem_t100 ?? undefined}>{n(v.t100_min, 2)}</td>
              <td className="num">{n(v.tf_bs_min, 1)}</td>
              <td className="num">{n(v.velocidade_maxima_bs_mm_min, 4)}</td>
              <td>
                {v.t100_min == null && v.t90_min == null
                  ? <Selo tom="neutro">Sem traçado</Selo>
                  : acima(v) ? <Selo tom="atencao">Acima da máxima</Selo>
                    : <Selo tom="ok">Dentro da máxima</Selo>}
              </td>
            </tr>
          ))}
        </tbody>
      </table></div>
      <p className="explicacao">
        Velocidades em mm/min. d_f = {n(d.cabecalho?.deslocamento_ruptura_estimado_mm, 2, true)} mm
        (etapa «Amostra»). Passe o mouse sobre um traço de t₉₀ ou t₁₀₀ para
        ver por que ele não saiu.
      </p>
    </>
  );
}

/**
 * Os quatro critérios nesta curva, lado a lado, nas duas bases — o
 * adotado em destaque. Adotar grava o critério do CP; o manual se aponta
 * clicando na curva, logo acima.
 */
function AnaliseDosCriterios({ cp, cab, gravar, salvando }: {
  cp: CorpoDeProvaCd; cab: any; gravar: Gravar; salvando: string | null;
}) {
  const nominais = (cp.criterios ?? []).filter((k: any) => k.base === 'nominal');
  if (!nominais.length) return null;
  const corr = (c: string) => (cp.criterios ?? []).find(
    (k: any) => k.base === 'corrigida' && k.criterio === c);
  const proprio = cp.cp.criterio_ruptura;
  return (
    <div className="ruptura-cartao">
      <h3>Análise dos critérios de ruptura — {cp.cp.identificacao}</h3>
      <p className="explicacao">
        {proprio
          ? `Este corpo de prova rompe por ${CRITERIO_TEXTO[proprio] ?? proprio}.`
          : cab?.criterio_ruptura
            ? `Sem critério próprio, vale o do ensaio: ${CRITERIO_TEXTO[cab.criterio_ruptura]}.`
            : 'Nem o corpo de prova nem o ensaio declararam critério — e sem ele não há ponto de ruptura nem envoltória.'}
        {' '}Cada linha é a leitura que o critério escolhe — nenhum interpola.
      </p>
      <div className="rolar-lado"><table className="dados">
        <thead><tr>
          <th>Critério</th><th>Referência</th>
          <th className="num">δh (mm)</th><th className="num">δh/L (%)</th>
          <th className="num">σ (kPa)</th><th className="num">τ (kPa)</th>
          <th className="num">σ_c (kPa)</th><th className="num">τ_c (kPa)</th>
          <th />
        </tr></thead>
        <tbody>
          {nominais.map((k: any) => {
            const c = corr(k.criterio);
            return (
              <tr key={k.criterio} className={k.adotado ? 'ruptura' : ''}>
                <td>{k.criterio_nome} <small>({SIGLA_CRITERIO_CD[k.criterio] ?? k.criterio})</small></td>
                <td><small>{k.referencia}</small></td>
                {k.ordem_leitura == null ? (
                  <td colSpan={6}><small>{k.motivo}</small></td>
                ) : (
                  <>
                    <td className="num">{n(k.deslocamento_horizontal_mm, 3)}</td>
                    <td className="num">{n(k.deslocamento_relativo_pct, 2)}</td>
                    <td className="num">{n(k.sigma_kpa, 2)}</td>
                    <td className="num forte">{n(k.tau_kpa, 2)}</td>
                    <td className="num">{n(c?.sigma_kpa, 2)}</td>
                    <td className="num">{n(c?.tau_kpa, 2)}</td>
                  </>
                )}
                <td>
                  {k.adotado ? <b>Adotado</b> : (
                    <button type="button" className="discreto"
                      disabled={salvando === 'criterio' || k.ordem_leitura == null}
                      onClick={() => gravar('criterio', () => editarCisalhamentoDiretoCp(
                        cp.cp.id, { criterioRuptura: k.criterio }))}>
                      Adotar
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table></div>
      {proprio && (
        <button type="button" className="discreto" disabled={salvando === 'criterio'}
          onClick={() => gravar('criterio', () => editarCisalhamentoDiretoCp(
            cp.cp.id, { criterioRuptura: null, rupturaDeslocamentoMm: null }))}>
          Voltar ao critério do ensaio
        </button>
      )}
    </div>
  );
}

// ── A envoltória ────────────────────────────────────────────────────

function Envoltoria({ d, id, gravar, salvando }: {
  d: EnsaioCisalhamentoDireto; id: string; gravar: Gravar; salvando: string | null;
}) {
  const cab = d.cabecalho;
  const lista = d.envoltoriasCriterios ?? [];
  const inicial = useMemo(() => (cab?.criterios_no_laudo ?? []) as string[],
    [cab?.criterios_no_laudo]);
  const [marcados, setMarcados] = useState<string[]>(inicial);
  const [baseLaudo, setBaseLaudo] = useState<'nominal' | 'ambas'>(cab?.base_no_laudo ?? 'nominal');
  const [comparar, setComparar] = useState<{ criterio: string; base: string }[]>([]);
  useEffect(() => { setMarcados(inicial); }, [inicial]);
  useEffect(() => { setBaseLaudo(cab?.base_no_laudo ?? 'nominal'); }, [cab?.base_no_laudo]);

  const porCriterio = useMemo(() => {
    const m = new Map<string, { nome: string; referencia: string; nominal?: any; corrigida?: any }>();
    for (const e of lista) {
      const g = m.get(e.criterio) ?? { nome: e.criterio_nome, referencia: e.referencia };
      (g as any)[e.base] = e;
      m.set(e.criterio, g);
    }
    return [...m.entries()];
  }, [lista]);
  const mudou = marcados.slice().sort().join() !== inicial.slice().sort().join()
    || baseLaudo !== (cab?.base_no_laudo ?? 'nominal');
  const alternar = (c: string) => setMarcados((m) =>
    (m.includes(c) ? m.filter((x) => x !== c) : [...m, c]));
  const env = (b: string) => (d.envoltorias ?? []).find((e: any) => e.base === b) ?? null;
  const celulas = (e: any) => (!e ? <td colSpan={3}>—</td> : e.motivo_sem_envoltoria
    ? <td colSpan={3} className="nada motivo">{e.motivo_sem_envoltoria}</td>
    : (
      <>
        <td className="num">{n(e.coesao_kpa, 2)}</td>
        <td className="num">{n(e.phi_graus, 2)}</td>
        <td className="num">{n(e.r2, 4)}</td>
      </>
    ));

  return (
    <>
      <section className="bloco">
        <h2>Envoltória de resistência</h2>
        <p className="explicacao">
          A reta τ = c + σ·tg φ ajustada por mínimos quadrados aos pontos de
          ruptura. A NOMINAL (ASTM D3080) é o resultado; a CORRIGIDA — τ e σ
          divididos juntos pela área de contato — vai ao lado no laudo se
          você pedir abaixo.
        </p>
        <dl className="grade-indices destaque">
          {(['nominal', 'corrigida'] as const).map((b) => {
            const e = env(b);
            return e && !e.motivo_sem_envoltoria ? (
              <div key={b} className="indice grande">
                <dt>{b === 'nominal' ? 'Nominal' : 'Corrigida'} — φ (°) · c (kPa)</dt>
                <dd>{n(e.phi_graus, 2)} · {n(e.coesao_kpa, 2)}</dd>
                <p className="sub">{e.pontos} pontos · R² {n(e.r2, 4)}</p>
              </div>
            ) : (
              <div key={b} className="indice">
                <dt>{b === 'nominal' ? 'Nominal' : 'Corrigida'}</dt>
                <dd>—</dd>
                <p className="sub">{e?.motivo_sem_envoltoria ?? 'nenhum ponto de ruptura'}</p>
              </div>
            );
          })}
        </dl>
        <GraficoDaEnvoltoriaCd d={d} comparar={comparar} />
      </section>

      <section className="bloco">
        <h2>Envoltórias por critério de ruptura</h2>
        <p className="explicacao">
          Cada linha aplica um critério a todos os corpos de prova e ajusta a
          mesma reta, nas duas bases. «Comparar» desenha a reta tracejada no
          gráfico acima; «No laudo» manda a linha para o relatório final. A
          envoltória adotada continua no laudo de qualquer forma.
        </p>
        <div className="rolar-lado"><table className="dados">
          <thead>
            <tr>
              <th rowSpan={2}>No laudo</th><th rowSpan={2}>Critério</th>
              <th colSpan={3}>Nominal</th><th colSpan={3}>Corrigida</th>
              <th rowSpan={2}>Comparar</th>
            </tr>
            <tr>
              <th className="num">c (kPa)</th><th className="num">φ (°)</th><th className="num">R²</th>
              <th className="num">c (kPa)</th><th className="num">φ (°)</th><th className="num">R²</th>
            </tr>
          </thead>
          <tbody>
            {porCriterio.map(([criterio, g]) => (
              <tr key={criterio}>
                <td>
                  <input type="checkbox" aria-label={`${g.nome} no laudo`}
                    checked={marcados.includes(criterio)}
                    disabled={!!g.nominal?.motivo_sem_envoltoria && !marcados.includes(criterio)}
                    onChange={() => alternar(criterio)} />
                </td>
                <td>{g.nome}<br /><small>{g.referencia}</small></td>
                {g.nominal?.motivo_sem_envoltoria && g.corrigida?.motivo_sem_envoltoria
                  ? <td colSpan={6} className="nada motivo">{g.nominal.motivo_sem_envoltoria}</td>
                  : <>{celulas(g.nominal)}{celulas(g.corrigida)}</>}
                <td>
                  {(['nominal', 'corrigida'] as const).map((b) => {
                    const e = g[b];
                    if (!e || e.motivo_sem_envoltoria) return null;
                    const on = comparar.some((x) => x.criterio === criterio && x.base === b);
                    return (
                      <label key={b} className="opcao-marcar">
                        <input type="checkbox" checked={on}
                          onChange={() => setComparar((v) => (on
                            ? v.filter((x) => !(x.criterio === criterio && x.base === b))
                            : [...v, { criterio, base: b }]))} />
                        {b === 'nominal' ? 'nom.' : 'corr.'}
                      </label>
                    );
                  })}
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
        <Escolha rotulo="Tensões no laudo"
          opcoes={[
            { valor: 'nominal', texto: 'Só a nominal', detalhe: 'ASTM D3080' },
            { valor: 'ambas', texto: 'Nominal e corrigida ao lado', detalhe: 'Nota 18 da D3080: declarada' },
          ]}
          valor={baseLaudo} aoEscolher={(v) => setBaseLaudo((v as 'nominal' | 'ambas') ?? 'nominal')}
          limpavel={false} />
        <button type="button" className="primario"
          disabled={!mudou || salvando === 'no-laudo'}
          onClick={() => gravar('no-laudo',
            () => gravarCriteriosNoLaudoCd(id, marcados, baseLaudo))}>
          {salvando === 'no-laudo' ? 'Gravando…' : 'Gravar a escolha do relatório'}
        </button>
      </section>

      <section className="bloco">
        <h2>Os pontos que sustentam a envoltória</h2>
        <div className="rolar-lado"><table className="dados">
          <thead><tr>
            <th>CP</th><th>Critério</th><th className="num">δh (mm)</th>
            <th className="num">δh/L (%)</th>
            <th className="num">σ (kPa)</th><th className="num">τ (kPa)</th>
            <th className="num">σ_c (kPa)</th><th className="num">τ_c (kPa)</th>
          </tr></thead>
          <tbody>
            {(d.rupturas ?? []).filter((r: any) => r.base === 'nominal').map((r: any) => {
              const c = (d.rupturas ?? []).find((x: any) => x.cp_id === r.cp_id
                && x.base === 'corrigida');
              return (
                <tr key={r.cp_id} className={r.situacao === 'descartado' ? 'descartada' : ''}>
                  <td>{r.identificacao}</td>
                  {r.motivo_sem_ruptura ? (
                    <td colSpan={7} className="nada">sem ponto de ruptura — {r.motivo_sem_ruptura}</td>
                  ) : (
                    <>
                      <td>{SIGLA_CRITERIO_CD[r.criterio] ?? r.criterio}</td>
                      <td className="num">{n(r.deslocamento_horizontal_mm, 3)}</td>
                      <td className="num">{n(r.deslocamento_relativo_pct, 2)}</td>
                      <td className="num">{n(r.sigma_kpa, 2)}</td>
                      <td className="num forte">{n(r.tau_kpa, 2)}</td>
                      <td className="num">{n(c?.sigma_kpa, 2)}</td>
                      <td className="num">{n(c?.tau_kpa, 2)}</td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table></div>
      </section>
    </>
  );
}
